"use client";

import Link from "next/link";
import { useCallback, useRef, useState } from "react";

type Props = {
  /** e.g. blog-index | blog-article | blog-footer */
  source?: string;
  variant?: "banner" | "inline";
};

export function BlogWeeklyLedgerSignup({ source = "blog", variant = "banner" }: Props) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "ok" | "err">("idle");
  const [msg, setMsg] = useState<string | null>(null);
  const hpRef = useRef<HTMLInputElement>(null);

  const submit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setStatus("loading");
      setMsg(null);
      try {
        const res = await fetch("/api/blog/weekly-ledger", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: email.trim(),
            source,
            companyWebsite: hpRef.current?.value ?? "",
          }),
        });
        const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
        if (res.status === 429) {
          setStatus("err");
          setMsg(data.error || "Too many attempts. Please try again later.");
          return;
        }
        if (!res.ok || !data.ok) {
          setStatus("err");
          setMsg(data.error || "Could not subscribe right now.");
          return;
        }
        setStatus("ok");
        setMsg("You’re on the list — watch your inbox on Thursday mornings.");
        setEmail("");
      } catch {
        setStatus("err");
        setMsg("Network error — try again in a moment.");
      }
    },
    [email, source],
  );

  const wrap = variant === "banner" ? "ed-blog-ledger ed-blog-ledger--banner" : "ed-blog-ledger ed-blog-ledger--inline";

  return (
    <section className={wrap} aria-labelledby="weekly-ledger-heading">
      <div className="ed-blog-ledger__inner">
        <div className="ed-blog-ledger__copy">
          <p className="ed-blog-ledger__kicker">The Weekly Ledger</p>
          <h2 id="weekly-ledger-heading" className="ed-blog-ledger__title">
            Inventory &amp; logistics signal — every Thursday
          </h2>
          <p className="ed-blog-ledger__sub">
            Working-capital moves, waste prevention, and operator-grade analysis. No noise — built for people who run shelves, not slides.
          </p>
        </div>
        <form className="ed-blog-ledger__form" onSubmit={(e) => void submit(e)} noValidate>
          <input
            ref={hpRef}
            type="text"
            name="company_website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="ed-blog-hp"
            defaultValue=""
          />
          <label className="ed-blog-ledger__label" htmlFor="weekly-ledger-email">
            Email
          </label>
          <div className="ed-blog-ledger__row">
            <input
              id="weekly-ledger-email"
              name="email"
              type="email"
              autoComplete="email"
              required
              placeholder="you@company.com"
              className="ed-blog-ledger__input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={status === "loading"}
            />
            <button type="submit" className={`ed-blog-ledger__btn${status === "loading" ? " ed-blog-ledger__btn--busy" : ""}`} disabled={status === "loading"}>
              {status === "loading" ? "Joining…" : "Join the Ledger"}
            </button>
          </div>
          {msg ? <p className={`ed-blog-ledger__msg ${status === "err" ? "ed-blog-ledger__msg--err" : ""}`}>{msg}</p> : null}
          <p className="ed-blog-ledger__fineprint">
            We respect your inbox — unsubscribe anytime. Marketing email requires consent under POPIA; see our{" "}
            <Link href="/privacy" className="ed-blog-ledger__fineprint-a">
              Privacy Policy
            </Link>
            .
          </p>
        </form>
      </div>
    </section>
  );
}
